import type { MediaImage } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import type { ReactiveDataDocument } from '@warp-drive/core/reactive';
import { withReactiveResponse } from '@warp-drive/core/request';
import type { RequestInfo } from '@warp-drive/core/types/request';
import { query } from '@warp-drive/utilities/json-api';

import { page, sort } from '../../data/json-api.ts';
import { jsonApiOptions } from '../../lib/json-api.ts';
import type MediaImagesService from '../../services/media-images';

interface SearchImagesSignature {
  Element: HTMLDivElement;
  Args: {
    legend: string;
    state: Readonly<SearchImagesState>;
    setState: (state: Partial<SearchImagesState>) => void;
  };
  Blocks: {
    query: [RequestInfo<ReactiveDataDocument<MediaImage[]>>];
  };
}

export class SearchImagesState {
  @tracked pageIndex = 0;
  @tracked pageSize = 12;
  @tracked textFilter = '';
}

export class SearchImages extends Component<SearchImagesSignature> {
  @service('media-images') declare mediaImages: MediaImagesService;
  @service declare store: Store;

  get query() {
    const filter: Record<string, unknown> = {
      'filter[original.url][$exists]': true,
      'filter[legacy][$ne]': true,
      'filter[hidden][$ne]': true,
    };

    // Add text search if filter is present (using committedTextFilter)
    if (this.args.state.textFilter) {
      filter['filter[$text][$search]'] = this.args.state.textFilter;
    }

    // Sort by relevance (rank) when searching, otherwise by modified date
    const sortField = this.args.state.textFilter ? '-rank' : '-modified';

    return withReactiveResponse<MediaImage[]>(
      query(
        DataTypeContent.MediaImage,
        {
          ...filter,
          ...page({
            limit: this.args.state.pageSize,
            offset: this.args.state.pageIndex * this.args.state.pageSize,
          }),
          ...sort(sortField),
        },
        jsonApiOptions
      )
    );
  }

  updateTextFilter = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    this.args.setState({
      textFilter: target.value,
      pageIndex: 0,
    });
  };

  clearSearch = (): void => {
    this.args.setState({
      textFilter: '',
      pageIndex: 0,
    });
  };

  <template>
    <fieldset>
      <legend class="gud-eyebrow">{{@legend}}</legend>
      <input
        id="imageBrowserTextFilter"
        type="text"
        class="form-control"
        value={{@state.textFilter}}
        placeholder='Search by title, description, or tags (e.g. "office", "leadership")...'
        {{debounce this.updateTextFilter}}
      />
    </fieldset>

    {{yield this.query to="query"}}
  </template>
}
